<template>
  <!-- Portada. HTML + CSS plano (sin v-carousel / v-infinite-scroll / v-row) para que funcione igual en Windows 7. -->
  <div class="ps-home">
    <!-- ===================================================== -->
    <!-- HERO -->
    <!-- ===================================================== -->
    <section v-if="model.hero?.length" class="ps-hero" aria-roledescription="carrusel">
      <div
        v-for="(item, index) in model.hero"
        :key="item.id ?? `hero-${index}`"
        class="ps-hero__slide"
        :class="{ 'ps-hero__slide--active': index === heroIndex }"
        :aria-hidden="index === heroIndex ? 'false' : 'true'"
      >
        <img :src="item.image" :alt="`Hero ${index + 1}`" />
      </div>

      <template v-if="model.hero.length > 1">
        <button type="button" class="ps-hero__arrow ps-hero__arrow--prev" aria-label="Anterior" @click="goHero(heroIndex - 1)">
          <i class="mdi mdi-chevron-left" aria-hidden="true"></i>
        </button>
        <button type="button" class="ps-hero__arrow ps-hero__arrow--next" aria-label="Siguiente" @click="goHero(heroIndex + 1)">
          <i class="mdi mdi-chevron-right" aria-hidden="true"></i>
        </button>

        <div class="ps-hero__dots">
          <button
            v-for="(item, index) in model.hero"
            :key="`dot-${index}`"
            type="button"
            class="ps-hero__dot"
            :class="{ 'ps-hero__dot--active': index === heroIndex }"
            :aria-label="`Ir a la imagen ${index + 1}`"
            @click="goHero(index)"
          ></button>
        </div>
      </template>
    </section>

    <!-- ===================================================== -->
    <!-- BENEFICIOS -->
    <!-- ===================================================== -->
    <section class="ps-benefits">
      <div class="ps-benefits__inner">
        <div v-for="benefit in benefits" :key="benefit.title" class="ps-benefit">
          <span class="ps-benefit__icon"><i class="mdi" :class="benefit.icon" aria-hidden="true"></i></span>
          <div>
            <div class="ps-benefit__title">{{ benefit.title }}</div>
            <div class="ps-benefit__subtitle">{{ benefit.subtitle }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===================================================== -->
    <!-- PRODUCTOS POR CATEGORÍA -->
    <!-- ===================================================== -->
    <div class="ps-home__content">
      <section v-for="category in categoriesWithProducts" :key="category.id" class="ps-category">
        <div class="ps-category__head">
          <div>
            <h2 class="ps-category__title">{{ category.name.toUpperCase() }}</h2>
            <div class="ps-category__bar"></div>
          </div>

          <a class="ps-category__all" :href="'/Home/Category?idCategory=' + category.id">
            Ver todos
            <i class="mdi mdi-arrow-right" aria-hidden="true"></i>
          </a>
        </div>

        <div class="ps-category__row">
          <button v-if="overflowing[category.id]" type="button" class="ps-category__arrow" aria-label="Anteriores" @click="scrollCategory(category.id, -1)">
            <i class="mdi mdi-chevron-left" aria-hidden="true"></i>
          </button>

          <div :ref="(el) => setCarouselRef(category.id, el)" class="ps-category__track">
            <div v-for="product in category.products" :key="product.id" class="ps-category__item">
              <product-card :product="product"></product-card>
            </div>
          </div>

          <button v-if="overflowing[category.id]" type="button" class="ps-category__arrow" aria-label="Siguientes" @click="scrollCategory(category.id, 1)">
            <i class="mdi mdi-chevron-right" aria-hidden="true"></i>
          </button>
        </div>
      </section>

      <!-- Marca para cargar la siguiente categoría al llegar al final -->
      <div v-if="hasMoreCategories" ref="sentinel" class="ps-home__sentinel">
        <span class="ps-spinner" role="status" aria-label="Cargando"></span>
      </div>

      <!-- SIN PRODUCTOS -->
      <div v-if="!categoriesWithProducts.length && !hasMoreCategories" class="ps-home__empty">
        <i class="mdi mdi-store-outline" aria-hidden="true"></i>
        <span>No hay productos disponibles</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
const globalStore = window.useGlobalStore();

// =====================================================
// MODEL
// =====================================================

const model = ref(window.VMData);

// =====================================================
// HERO
// =====================================================

const heroIndex = ref(0);
let heroTimer = null;

const goHero = (index) => {
  const total = model.value.hero?.length || 0;
  if (!total) return;
  heroIndex.value = (index + total) % total;
  startHeroTimer();
};

const startHeroTimer = () => {
  clearInterval(heroTimer);
  if ((model.value.hero?.length || 0) > 1) {
    heroTimer = setInterval(() => {
      heroIndex.value = (heroIndex.value + 1) % model.value.hero.length;
    }, 6000);
  }
};

// =====================================================
// BENEFICIOS
// =====================================================

const benefits = [
  {
    icon: "mdi-truck-fast-outline",
    title: "Envío a todo el país",
    subtitle: "Recibí tu pedido en la puerta de tu casa",
  },
  {
    icon: "mdi-autorenew",
    title: "Cambios y devoluciones",
    subtitle: "Tenés 30 días para hacerlo sin costo",
  },
  {
    icon: "mdi-credit-card-outline",
    title: "Medios de pago",
    subtitle: "Tarjetas, transferencia y efectivo",
  },
  {
    icon: "mdi-shield-check-outline",
    title: "Compra 100% segura",
    subtitle: "Tus datos siempre protegidos",
  },
];

// =====================================================
// CARGA PROGRESIVA DE CATEGORÍAS
// =====================================================

const nextCategoryIndex = ref(1);
const loadingCategory = ref(false);
const sentinel = ref(null);
let observer = null;
let sentinelTimer = null;

const hasMoreCategories = computed(
  () => !!model.value?.categories && nextCategoryIndex.value < model.value.categories.length,
);

const loadNextCategory = async () => {
  if (loadingCategory.value || !hasMoreCategories.value) return;

  loadingCategory.value = true;

  try {
    const category = model.value.categories[nextCategoryIndex.value];
    const categoryId = category.value ?? category.Id;

    const products = await globalStore.getProductsByCategory(categoryId, 10);

    if (products?.length) {
      model.value.products.push(...products);
    }
  } catch (error) {
    console.error(error);
  } finally {
    // aunque falle una categoría se sigue con la siguiente (antes la carga quedaba trabada)
    nextCategoryIndex.value++;
    loadingCategory.value = false;
  }
};

// Sin IntersectionObserver (navegadores muy viejos) se cargan todas de una vez.
const watchSentinel = () => {
  if (!("IntersectionObserver" in window)) {
    (async () => {
      while (hasMoreCategories.value) await loadNextCategory();
    })();
    return;
  }

  observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadNextCategory();
    },
    { rootMargin: "300px 0px" },
  );

  // el elemento aparece y desaparece según queden categorías: se re-observa en cada cambio
  const observeIfPresent = () => {
    observer.disconnect();
    if (sentinel.value) observer.observe(sentinel.value);
  };

  sentinelTimer = setInterval(observeIfPresent, 700);
  observeIfPresent();
};

onMounted(() => {
  startHeroTimer();
  watchSentinel();
  window.addEventListener("resize", updateOverflow);
  nextTick(updateOverflow);
});

watch(
  () => model.value.products?.length,
  () => nextTick(updateOverflow),
);

onBeforeUnmount(() => {
  clearInterval(heroTimer);
  clearInterval(sentinelTimer);
  window.removeEventListener("resize", updateOverflow);
  observer?.disconnect();
});

// =====================================================
// CATEGORÍAS + PRODUCTOS
// =====================================================

const categoriesWithProducts = computed(() => {
  if (!model.value?.categories?.length) {
    return [];
  }

  return model.value.categories
    .map((category) => {
      const categoryId = category.value ?? category.Id;

      return {
        id: categoryId,

        name:
          category.title ??
          category.Name ??
          category.text ??
          category.Text ??
          "",

        products: (model.value.products ?? []).filter(
          (product) => product.idProductCategory === categoryId,
        ),
      };
    })
    .filter((category) => category.products.length > 0);
});

// =====================================================
// SCROLL DEL CARRUSEL
// =====================================================

const carouselRefs = {};
// las flechas sólo aparecen si las tarjetas no entran en el ancho disponible
const overflowing = ref({});

const updateOverflow = () => {
  const next = {};
  for (const [id, el] of Object.entries(carouselRefs)) next[id] = !!el && el.scrollWidth > el.clientWidth + 2;
  overflowing.value = next;
};

const setCarouselRef = (categoryId, element) => {
  if (element) carouselRefs[categoryId] = element;
};

const scrollCategory = (categoryId, direction) => {
  const element = carouselRefs[categoryId];
  if (!element) return;

  const amount = element.clientWidth * 0.8 * direction;
  if (typeof element.scrollBy === "function") element.scrollBy({ left: amount, behavior: "smooth" });
  else element.scrollLeft += amount;
};
</script>

<style scoped>
.ps-home {
  width: 100%;
  overflow: hidden;
}

/* ---------- Hero ---------- */
.ps-hero {
  position: relative;
  width: 100%;
  /* Los banners son 16:9 (1920 × 1080) con textos y botones: se muestran enteros, sin recortar. En pantallas anchas el alto se limita y el
     banner queda centrado. */
  aspect-ratio: 16 / 9;
  max-height: 78vh;
  max-width: calc(78vh * 16 / 9);
  margin: 0 auto;
  overflow: hidden;
  background: #124b4e;
}

.ps-hero__slide {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.6s ease, visibility 0s linear 0.6s;
}

.ps-hero__slide--active {
  opacity: 1;
  visibility: visible;
  transition-delay: 0s;
}

.ps-hero__slide img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.ps-hero__arrow {
  position: absolute;
  top: 50%;
  width: 44px;
  height: 44px;
  margin-top: -22px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(26, 26, 0, 0.45);
  color: #f4efe6;
  font-size: 28px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease, background 0.15s ease;
}

.ps-hero:hover .ps-hero__arrow,
.ps-hero__arrow:focus-visible {
  opacity: 1;
}

.ps-hero__arrow:hover {
  background: rgba(26, 26, 0, 0.7);
}

.ps-hero__arrow--prev {
  left: 14px;
}

.ps-hero__arrow--next {
  right: 14px;
}

.ps-hero__dots {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 12px;
  display: flex;
  justify-content: center;
  gap: 8px;
}

.ps-hero__dot {
  width: 10px;
  height: 10px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(244, 239, 230, 0.6);
  cursor: pointer;
}

.ps-hero__dot--active {
  background: #a9d840;
  transform: scale(1.25);
}

/* ---------- Beneficios ---------- */
.ps-benefits {
  width: 100%;
  background: #f4efe6;
  border-bottom: 1px solid rgba(26, 26, 0, 0.1);
}

.ps-benefits__inner {
  display: flex;
  flex-wrap: wrap;
  gap: 14px 20px;
  box-sizing: border-box;
  max-width: 1200px;
  margin: 0 auto;
  padding: 14px 24px;
}

.ps-benefit {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 220px;
}

.ps-benefit__icon {
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(18, 75, 78, 0.16);
  color: #2f8a1c;
  font-size: 20px;
}

.ps-benefit__title {
  font-family: var(--ps-font-title, "Poppins", Arial, sans-serif);
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1.25;
}

.ps-benefit__subtitle {
  font-size: 12.5px;
  color: rgba(26, 26, 0, 0.62);
}

/* ---------- Categorías ---------- */
.ps-home__content {
  box-sizing: border-box;
  max-width: 1280px;
  margin: 0 auto;
  padding: 36px 24px 60px;
}

.ps-category {
  margin-bottom: 44px;
}

.ps-category__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.ps-category__title {
  margin: 0;
  font-family: var(--ps-font-title, "Poppins", Arial, sans-serif);
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
  color: #124b4e;
}

.ps-category__bar {
  width: 45px;
  height: 3px;
  margin-top: 8px;
  border-radius: 10px;
  background: #124b4e;
}

.ps-category__all {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  padding: 8px 14px;
  border-radius: 999px;
  color: #124b4e;
  font-family: var(--ps-font-title, "Poppins", Arial, sans-serif);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
}

.ps-category__all:hover {
  background: rgba(18, 75, 78, 0.16);
}

.ps-category__row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ps-category__arrow {
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(18, 75, 78, 0.18);
  color: #124b4e;
  font-size: 26px;
  cursor: pointer;
}

.ps-category__arrow:hover {
  background: #124b4e;
  color: #ffffff;
}

.ps-category__track {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: stretch;
  gap: 18px;
  box-sizing: border-box;
  padding: 18px 6px 22px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  scroll-snap-type: x proximity;
  scrollbar-width: none;
}

.ps-category__track::-webkit-scrollbar {
  display: none;
}

.ps-category__item {
  flex: 0 0 236px;
  scroll-snap-align: start;
}

.ps-home__sentinel {
  display: flex;
  justify-content: center;
  padding: 24px 0;
}

.ps-home__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 300px;
  color: rgba(26, 26, 0, 0.45);
}

.ps-home__empty .mdi {
  font-size: 65px;
}

/* ---------- Celular ---------- */
@media (max-width: 599px) {
  .ps-hero {
    aspect-ratio: 16 / 9;
  }

  .ps-hero__arrow {
    display: none;
  }

  .ps-benefits__inner {
    padding: 12px 16px;
  }

  /* dos por fila y más compactos */
  .ps-benefits__inner {
    gap: 12px 12px;
  }

  .ps-benefit {
    flex: 1 1 calc(50% - 12px);
    gap: 8px;
  }

  .ps-benefit__icon {
    flex-basis: 32px;
    width: 32px;
    height: 32px;
    font-size: 17px;
  }

  .ps-benefit__title {
    font-size: 12.5px;
  }

  .ps-benefit__subtitle {
    font-size: 11.5px;
  }

  .ps-home__content {
    padding: 28px 16px 48px;
  }

  .ps-category__title {
    font-size: 21px;
  }

  /* en el celular se desliza con el dedo: sin flechas, dos tarjetas por pantalla */
  .ps-category__arrow {
    display: none;
  }

  .ps-category__track {
    margin: 0 -16px;
    padding: 18px 16px 22px;
    gap: 14px;
  }

  .ps-category__item {
    flex-basis: calc((100% - 14px) / 2 - 16px);
    min-width: 150px;
  }
}
</style>
